import React from 'react';
import { withRouter } from 'react-router-dom';

import http from 'api/http';
import { userAPI } from 'api/requests';
import config from 'config';

import LoginPasswordInput from './LoginPasswordInput';
import ManufacturerSelect from './ManufacturerSelect';

import NotificationManager from 'dom-managers/notification';
import {
  buildAuthorizeUrl,
  exchangeCodeForToken,
  consumePkceVerifierForState,
} from 'api/communityHubOAuth';

@withRouter
export default class LoginScene extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: 'none', // 'none' | 'manufacturerSelect'
      manufacturerList: [],
      requestInProcess: false,

      oauthAccessToken: null,
      oauthUserId: null,
    };
  }

  componentDidMount() {
    const code = this.getQueryStringVariable('code');
    const returnedState = this.getQueryStringVariable('state');
    const error = this.getQueryStringVariable('error');

    if (error) {
      this.clearOAuthParamsFromUrl();
      NotificationManager.error(401, error);
      return;
    }

    if (code) {
      // Run callback processing automatically.
      this.handleOAuthCallback({ code, returnedState });
    }
  }

  getRedirectUri = () => `${window.location.origin}/`;

  clearOAuthParamsFromUrl = () => {
    const { history, location } = this.props;
    history.replace(location.pathname);
  };

  handleOAuthCallback = ({ code, returnedState }) => {
    const redirectUri = this.getRedirectUri();

    this.setState({ requestInProcess: true }, () => {
      Promise.resolve()
        .then(() => {
          const codeVerifier = consumePkceVerifierForState(returnedState);

          return exchangeCodeForToken({
            tokenEndpoint: config.communityHub.tokenEndpoint,
            redirectUri,
            code,
            codeVerifier,
          });
        })
        .then(({ data }) => {
          const accessToken = data.access_token;
          if (!accessToken) throw new Error('Missing access token from token response');

          // Fetch userInfo to get AccountID (NOT in token response)
          return http
            .get(userAPI.info, { headers: { Authorization: `Bearer ${accessToken}` } })
            .then(({ data: userInfo }) => {
              const accountId =
                userInfo &&
                userInfo.id; 

              if (!accountId) throw new Error('Unable to determine AccountID from userInfo');

              this.setState({ oauthAccessToken: accessToken });

              // Use AccountID to look up manufacturers
              return http.get(userAPI.getManufacturersByAccountId({ accountId }), {
                headers: { Authorization: `Bearer ${accessToken}` },
              });
            });
        })
        .then(({ data: manufacturerList }) => {
          this.clearOAuthParamsFromUrl();

          if (!manufacturerList || manufacturerList.length < 2) {
            return this.props
              .signIn({ accessToken: this.state.oauthAccessToken })
              .finally(() => this.setState({ requestInProcess: false }));
          }

          this.setState({
            mode: 'manufacturerSelect',
            manufacturerList,
            requestInProcess: false,
          });
        })
        .catch(err => {
          this.setState({ requestInProcess: false });

          const message =
            (err &&
              err.response &&
              err.response.data &&
              (err.response.data.error_description || err.response.data.error)) ||
            (err && err.message) ||
            'OAuth login failed';

          NotificationManager.error(401, String(message).replace(/\+/g, ' '));
        });
    });
  };

  handleSignInClick = event => {
    event.preventDefault();
    if (event.nativeEvent) event.nativeEvent.preventDefault();

    const { mode, oauthAccessToken } = this.state;

    if (mode === 'manufacturerSelect') {
      this.setState({ requestInProcess: true }, () => {
        this.props
          .signIn({ accessToken: oauthAccessToken }, event.mnfCode)
          .finally(() => this.setState({ requestInProcess: false }));
      });
      return;
    }

    const redirectUri = this.getRedirectUri();

    this.setState({ requestInProcess: true }, async () => {
      try {
        const authorizeUrl = await buildAuthorizeUrl({
          authorizationEndpoint: config.communityHub.authorizationEndpoint,
          clientId: config.communityHub.clientId,
          redirectUri,
        });

        window.location.assign(authorizeUrl);
      } catch (e) {
        this.setState({ requestInProcess: false });
        NotificationManager.error(500, e && e.message ? e.message : 'Unable to start OAuth login');
      }
    });
  };

  getQueryStringVariable(variable) {
    const urlParams = new URLSearchParams(this.props.location.search);
    return urlParams.get(variable);
  }

  render() {
    const { mode, manufacturerList, requestInProcess } = this.state;

    return (
      <main className='login-scene'>
        <section>
          <figure></figure>
          <div className='fields-wrapper'>
            {mode === 'none' && (
              <LoginPasswordInput
                requestInProcess={requestInProcess}
                handleSignInClick={this.handleSignInClick}
              />
            )}

            {mode === 'manufacturerSelect' && (
              <ManufacturerSelect
                toggleMode={() => this.setState({ mode: 'none' })}
                handleSignInClick={this.handleSignInClick}
                requestInProcess={requestInProcess}
                manufacturerList={manufacturerList}
              />
            )}
          </div>
        </section>
      </main>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/LoginScene/index.jsx